// ADR 23.9.26v §3 — TanStack's three-method `Persister` over `offlineDb`, key
// `query-cache`. The dehydrated object is stored as a structured clone, never
// stringified.
//
// Two rules live here rather than at the call site:
//
// - **Throttled, trailing, one write per second.** The subscription fires on
//   every cache event; the last state of a burst is the one written.
// - **No write without a live session.** Sign-out is a `window` event and does
//   not reach a second tab, so without this guard a still-open second tab would
//   rewrite the departing learner's plans on its next cache event. The check is
//   made when the write *fires*, so a write pending across a sign-out is dropped.

import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client'

import { readRefreshToken } from '../auth/tokenStore'
import { offlineDb, type OfflineKv } from './offlineDb'

export const QUERY_CACHE_KEY = 'query-cache'
export const PERSIST_THROTTLE_MS = 1000

export interface PersisterOptions {
  store?: OfflineKv
  hasSession?: () => boolean
  /** Called after each write lands — §3's `navigator.storage.persist()` hook. */
  onWritten?: () => void
}

/** TanStack's `Persister`, plus a way to drop a write still waiting on the throttle. */
export interface ThrottledPersister extends Persister {
  cancel(): void
}

export function createPersister({
  store = offlineDb,
  hasSession = () => readRefreshToken() !== null,
  onWritten,
}: PersisterOptions = {}): ThrottledPersister {
  let pending: PersistedClient | null = null
  let timer: ReturnType<typeof setTimeout> | null = null

  const cancel = () => {
    if (timer !== null) clearTimeout(timer)
    timer = null
    pending = null
  }

  const flush = () => {
    timer = null
    const client = pending
    pending = null
    if (client === null || !hasSession()) return
    void store.set(QUERY_CACHE_KEY, client).then(onWritten)
  }

  return {
    persistClient(client) {
      if (!hasSession()) return
      pending = client
      if (timer === null) timer = setTimeout(flush, PERSIST_THROTTLE_MS)
    },
    restoreClient: () => store.get<PersistedClient>(QUERY_CACHE_KEY),
    removeClient() {
      cancel()
      return store.del(QUERY_CACHE_KEY)
    },
    cancel,
  }
}
